import { useEffect, useId, useRef, type KeyboardEvent } from 'react';

interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  busy: boolean;
  disabled?: boolean;
}

const MAX_HEIGHT = 160;

/** Campo de pregunta: Enter envia, Mayus+Enter hace un salto de linea. */
export function ChatComposer({
  value,
  onChange,
  onSend,
  busy,
  disabled = false,
}: ChatComposerProps): JSX.Element {
  const fieldId = useId();
  const hintId = useId();
  const ref = useRef<HTMLTextAreaElement>(null);

  // Crece con el texto hasta un tope, para que en el movil no ocupe la
  // pantalla entera pero tampoco haya que escribir a ciegas en una sola linea.
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, MAX_HEIGHT)}px`;
  }, [value]);

  const canSend = !busy && !disabled && value.trim() !== '';

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    if (canSend) onSend();
  }

  return (
    <form
      className="composer"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSend) onSend();
      }}
    >
      <label className="composer__label" htmlFor={fieldId}>
        Pregúntale a Alexia
      </label>
      <div className="composer__row">
        <textarea
          id={fieldId}
          ref={ref}
          className="input composer__input"
          rows={1}
          value={value}
          disabled={disabled}
          aria-describedby={hintId}
          placeholder="Por ejemplo: ¿por qué bajó mi beneficio el mes pasado?"
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button type="submit" className="btn composer__send" disabled={!canSend}>
          {busy ? 'Pensando…' : 'Preguntar'}
        </button>
      </div>
      <p className="composer__hint" id={hintId}>
        Pulsa Intro para enviar y Mayús + Intro para escribir en otra línea.
      </p>
    </form>
  );
}
